import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { CriticalStockMachine } from '../../../../lib/types';
import { formatPercentage } from '../../../../lib/export';
import { ArrowLeft } from 'lucide-react';

export function AllCriticalMachines() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CriticalStockMachine[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllMachines();
  }, []);

  const fetchAllMachines = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${brandId}/dashboard/stock/machines/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all critical machines');
      }

      const data = await response.json();
      setData(data);
    } catch (error) {
      console.error('Error fetching all critical machines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/brand/${brandId}/dashboard/stock`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stock Reporting
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">All Critical Stock Machines</h1>
            <p className="text-gray-600">{data.length} machines with critical stock levels</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Machine Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sub-Operator
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Level %
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RoS
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((machine) => {
                  const priority = machine.stockLevel < 10 ? 'Critical' : machine.stockLevel < 20 ? 'High' : 'Medium';
                  const priorityColor = machine.stockLevel < 10 ? 'bg-red-100 text-red-800' : 
                                       machine.stockLevel < 20 ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800';
                  
                  return (
                    <tr key={machine.machineId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {machine.machineName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {machine.subOperator}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={`font-medium ${
                          machine.stockLevel < 10 ? 'text-red-600' : 
                          machine.stockLevel < 20 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {formatPercentage(machine.stockLevel, 1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {machine.ros.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${priorityColor}`}>
                          {priority}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
